/**
 * Os canais da revenda, no formato da tela de caixas do motor de conversa
 * (29/09/2026).
 *
 * ── O formato ────────────────────────────────────────────────────────────
 *
 * O motor já conecta WhatsApp, Instagram, Facebook, Web Chat, API e TikTok, e
 * a tela dele é conhecida de quem atende: a lista das caixas, um botão de
 * adicionar, e ao adicionar uma GRADE de cartões — um por canal, com o logo, o
 * nome e uma linha do que ele faz. Escolhido o canal, um passo a passo curto.
 *
 * Aqui é a mesma tela, com o nosso desenho e sem o nome do motor.
 *
 * ── Um canal por vez ─────────────────────────────────────────────────────
 *
 * O cartão de um canal que ainda não conecta por aqui aparece, e diz "Em
 * breve". Esconder faria a grade mudar de forma a cada entrega, e a revenda
 * procuraria o canal que viu ontem.
 */

import type { ReactNode } from 'react';
import type { CanalConectado, TipoDeCanal } from '@cliqcrm/contracts';
import { Cartao, Chip, Icone, Selo, type NomeDoIcone } from '@/shared/ui';
import './canais.css';

export type CanalDoCatalogo = {
  tipo: TipoDeCanal;
  nome: string;
  descricao: string;
};

/**
 * A grade, na ordem em que a revenda procura: do mais usado ao menos.
 *
 * O Web Chat daqui é o da REVENDA, para o site dela (30/09/2026). O balão de
 * suporte da Cliqfy dentro do CliqCRM é outra coisa, e mora em
 * `app/casca/balao-de-suporte.tsx`.
 */
export const CATALOGO_DE_CANAIS: readonly CanalDoCatalogo[] = [
  { tipo: 'whatsapp', nome: 'WhatsApp', descricao: 'Atenda seus clientes pelo WhatsApp.' },
  {
    tipo: 'instagram',
    nome: 'Instagram',
    descricao: 'Conecte a conta do Instagram da sua empresa.',
  },
  { tipo: 'facebook', nome: 'Facebook', descricao: 'Conecte a página do Facebook da sua empresa.' },
  { tipo: 'web_chat', nome: 'Web Chat', descricao: 'Coloque um chat no site da sua empresa.' },
  { tipo: 'api', nome: 'API', descricao: 'Crie um canal próprio, ligado por API.' },
  { tipo: 'tiktok', nome: 'TikTok', descricao: 'Conecte a conta do TikTok da sua empresa.' },
];

const NOME_DO_TIPO: Readonly<Record<TipoDeCanal, string>> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  web_chat: 'Web Chat',
  api: 'API',
  tiktok: 'TikTok',
  telegram: 'Telegram',
  email: 'E-mail',
  sms: 'SMS',
  line: 'Line',
  outro: 'Outro canal',
};

export function nomeDoTipo(tipo: TipoDeCanal): string {
  return NOME_DO_TIPO[tipo];
}

const ICONE_DO_TIPO: Partial<Record<TipoDeCanal, NomeDoIcone>> = {
  whatsapp: 'whatsapp',
  instagram: 'instagram',
  facebook: 'facebook',
  web_chat: 'web-chat',
  api: 'api',
  tiktok: 'tiktok',
};

/** O glifo do canal; o de atendimento para o tipo que não tem desenho próprio. */
export function iconeDoCanal(tipo: TipoDeCanal): NomeDoIcone {
  return ICONE_DO_TIPO[tipo] ?? 'atendimento';
}

/** O logo do canal num quadrado com a cor da marca. */
export function LogoDoCanal({ tipo }: { tipo: TipoDeCanal }): ReactNode {
  return (
    <span className="logo-canal" data-tipo={tipo} aria-hidden="true">
      <Icone nome={iconeDoCanal(tipo)} />
    </span>
  );
}

/**
 * Os passos do assistente, como no motor: onde se está, e quantos faltam.
 *
 * Lista ordenada com `aria-current="step"`: o leitor de tela anuncia "passo 2
 * de 3", que é a informação que o desenho dá a quem vê.
 */
export function PassosDoAssistente<T extends string>({
  passos,
  atual,
}: {
  passos: readonly { chave: T; nome: string }[];
  atual: T;
}): ReactNode {
  const indice = passos.findIndex((p) => p.chave === atual);
  return (
    <ol className="passos-canal" aria-label="Passos para adicionar o canal">
      {passos.map((passo, i) => (
        <li
          key={passo.chave}
          className="passos-canal__passo"
          data-estado={i < indice ? 'feito' : i === indice ? 'atual' : 'depois'}
          aria-current={i === indice ? 'step' : undefined}
        >
          <span className="passos-canal__numero" aria-hidden="true">
            {i + 1}
          </span>
          {passo.nome}
        </li>
      ))}
    </ol>
  );
}

/**
 * A grade de escolha: um cartão por canal.
 *
 * O cartão inteiro é o botão. O que ainda não conecta por aqui fica
 * desabilitado, com o motivo no `title` e escrito no próprio cartão.
 */
export function GradeDeCanais({
  disponivel,
  aoEscolher,
}: {
  /** `null` quando o canal pode ser escolhido; senão, o motivo de não poder. */
  disponivel: (tipo: TipoDeCanal) => string | null;
  aoEscolher: (tipo: TipoDeCanal) => void;
}): ReactNode {
  return (
    <ul className="grade-canais">
      {CATALOGO_DE_CANAIS.map((canal) => {
        const motivo = disponivel(canal.tipo);
        return (
          <li key={canal.tipo}>
            <button
              type="button"
              className="grade-canais__cartao"
              disabled={motivo !== null}
              title={motivo ?? undefined}
              onClick={() => aoEscolher(canal.tipo)}
            >
              <LogoDoCanal tipo={canal.tipo} />
              <span className="grade-canais__nome">{canal.nome}</span>
              <span className="grade-canais__descricao">{canal.descricao}</span>
              {motivo !== null && <Chip>{motivo === EM_BREVE ? 'Em breve' : 'Indisponível'}</Chip>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** O motivo dos canais que ainda não conectam por esta tela. */
export const EM_BREVE = 'Este canal ainda não conecta por aqui.';

/**
 * `null` quando o canal pode ser adicionado por aqui; senão, o porquê.
 *
 * Uma função só para a grade do assistente e para a loja de funcionalidades
 * (#560): as duas dizem se o canal se adiciona, e duas cópias da regra
 * acabariam discordando — a loja oferecendo um canal que o assistente recusa.
 */
export function disponibilidadeDoCanal(
  tipo: TipoDeCanal,
  whatsappConfigurado: boolean,
): string | null {
  if (tipo === 'whatsapp') {
    return whatsappConfigurado ? null : 'Conectar WhatsApp não está disponível nesta conta.';
  }
  // O canal de API fica "Em breve" (29/09/2026): a borda ainda não publica a
  // API pública do motor, e sem ela o outro sistema não teria por onde falar.
  if (tipo === 'facebook' || tipo === 'instagram' || tipo === 'web_chat') return null;
  return EM_BREVE;
}

/** Um canal conectado que não tem cartão próprio: logo, nome, tipo e detalhe. */
export function CartaoDoCanal({
  canal,
  acoes,
}: {
  canal: CanalConectado;
  acoes?: ReactNode;
}): ReactNode {
  return (
    <Cartao como="li" variante="linha">
      <LogoDoCanal tipo={canal.tipo} />
      <span className="lista-canais__nome">
        {canal.nome}
        <span className="lista-canais__tipo">{nomeDoTipo(canal.tipo)}</span>
      </span>
      {canal.detalhe && <span className="lista-canais__detalhe">{canal.detalhe}</span>}
      <Selo tom="ok">Conectado</Selo>
      {acoes}
    </Cartao>
  );
}
